import {getAdvancedConsoleUnitTestReference} from '../../models/unitTests/advancedConsole';
import {
  UnitTestLoaderParameters,
  UnitTestSchema,
  WithRunnerData,
} from '../UnitTestSchema';
import {getDoc} from 'firebase/firestore';
import {
  EMPTY_TEST_RESULT,
  getFirstTestFailed,
  UnitTestResult,
  UnitTestResults,
} from '../unitTestResults';
import {
  AdvancedAutograderResultTypes,
  AdvancedAutograderTrace,
  calculateMaxTrials,
  LoadedAdvancedConsoleUnitTestData,
  runTrial,
  verifyRandomVariables,
} from './advancedConsoleUtils';
import {AdvancedConsoleEditor} from './AdvancedConsoleEditor';
import type {TranslationFunction} from 'components/translation/utils';

async function loader({
  courseId,
  assignmentId,
}: UnitTestLoaderParameters): Promise<LoadedAdvancedConsoleUnitTestData | null> {
  const advancedConsoleUnitTestReference = getAdvancedConsoleUnitTestReference(
    courseId,
    assignmentId,
  );
  const advancedConsoleUnitTestDoc = await getDoc(
    advancedConsoleUnitTestReference,
  );
  if (!advancedConsoleUnitTestDoc.exists()) {
    return null;
  }
  const advancedConsoleUnitTests = advancedConsoleUnitTestDoc.data();
  return {
    unitTests: advancedConsoleUnitTests,
  };
}

async function runner({
  loadedData,
  pyodideClient,
  filesCode,
  fileStructure,
}: WithRunnerData<LoadedAdvancedConsoleUnitTestData>): Promise<UnitTestResults> {
  const {unitTests} = loadedData ?? {unitTests: []};
  if (!unitTests || unitTests.length === 0) {
    return EMPTY_TEST_RESULT;
  }
  // Iterate over each test case/"branch" (each has a different script)
  const unitTestResults: UnitTestResult[] = [];
  for (const branch of unitTests) {
    // ! Object for holding vars across iterations. Updated by SIDE EFFECT from `runTrial`
    // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    let randomVars = new Map();

    // ! This is updated after the first iteration. Uses recorded variables to calculate min trials for (default) tolerance=0.0001
    let trialsToRun = 1;

    // Track the last autograder trace so that on success or error conditions outside of the loop, we can add it to the result
    let lastAutograderTrace: AdvancedAutograderTrace = [];
    let foundError = false;

    // Run code `trialsToRun` times to make sure results are only falsely wrong with < `tolerance` probability
    for (let i = 0; i < trialsToRun; i++) {
      const {
        advancedAutograderState: {
          registeredVars,
          errorState,
          autogradeTrace,
          stopRequested,
        },
        error,
      } = await runTrial(
        pyodideClient,
        filesCode,
        fileStructure,
        branch.gradingScripts,
        randomVars,
      );

      lastAutograderTrace = autogradeTrace;

      // Check for "early stopping" (i.e. if the user clicked the stop button).
      if (stopRequested) {
        return {
          testResults: [],
          testsRun: false,
        };
      }

      // Check if autograder ran into an error
      if (errorState.get('has_error')) {
        // Return output/input log and autograder data for first iteration that ran into an error
        unitTestResults.push({
          isSuccess: false,
          name: AdvancedAutograderResultTypes.AdvancedAutograderError,
          usedAi: false,
          rawResult: {
            testName: branch.name,
            message: errorState.get('message'),
            autograderTrace: autogradeTrace,
          },
        });
        foundError = true;
        break; // Move to next branch
      }

      // Check if there is a Python runtime/syntax error, tell student to run the code
      if (error.length > 0) {
        unitTestResults.push({
          isSuccess: false,
          name: AdvancedAutograderResultTypes.PythonError,
          usedAi: false,
          rawResult: {
            testName: branch.name,
            message: error[0],
            autograderTrace: autogradeTrace,
          },
        });
        foundError = true;
        break; // Move to next branch
      }

      // If on first iteration, use observed random variables to update # iterations
      if (i === 0) {
        trialsToRun = calculateMaxTrials(registeredVars);
      }
    }

    // Check distribution of vars accumulated over all trials
    const {allCorrect: allRangesCorrect, errorMessage} =
      verifyRandomVariables(randomVars);

    // If we haven't found an error yet, check if the ranges are correct
    if (!foundError) {
      // Fail if some range is incorrect, don't run rest of the branches. Otherwise, move on to next branch!
      if (!allRangesCorrect) {
        unitTestResults.push({
          isSuccess: false,
          name: AdvancedAutograderResultTypes.RandomVarError,
          usedAi: false,
          rawResult: {
            message: errorMessage,
            testName: branch.name,
            autograderTrace: lastAutograderTrace,
          },
        });

        return {
          testResults: unitTestResults,
          testsRun: true,
        };
      } else {
        unitTestResults.push({
          isSuccess: true,
          name: branch.name,
          usedAi: false,
          rawResult: {
            message: 'Success for this branch!',
            autograderTrace: lastAutograderTrace,
          },
        });
      }
    }
  }

  // If no branches had incorrect ranges/other errors, we passed!
  return {
    testResults: unitTestResults,
    testsRun: true,
  };
}

function getFailureMessage(results: UnitTestResults, t: TranslationFunction) {
  const firstTestFailed = getFirstTestFailed(results);
  let text =
    "The program finish running, but something wasn't what we expected.";
  if (firstTestFailed === AdvancedAutograderResultTypes.PythonError) {
    text =
      "We couldn't run your program because we encountered an error. Run your program in the IDE to see what happened.";
  } else if (
    firstTestFailed === AdvancedAutograderResultTypes.AdvancedAutograderError
  ) {
    // The actual error given in the autograder script + more info will be in Test Center
    const message = results.testResults.find(
      result => result.name === firstTestFailed,
    )?.rawResult?.message;
    text = message.includes('infinite loop')
      ? message
      : "The program finished running, but your output isn't what we expected. Check the Test Center for more information.";
  } else if (firstTestFailed === AdvancedAutograderResultTypes.RandomVarError) {
    text =
      "Your program's output was correct, but there's an error in how you use randomness in your code. Check the Test Center for more details.";
  }

  return t(text) as string;
}

export const ADVANCED_CONSOLE_UNIT_TEST_SCHEMA: UnitTestSchema<LoadedAdvancedConsoleUnitTestData> =
  {
    loader,
    runner,
    EditorComponent: AdvancedConsoleEditor,
    unitTestType: 'advanced_console',
    displayName: 'Reactive Console Test',
    tooltipDescription:
      'Runs a stateful console test that can react to student input.',
    getFailureMessage,
  };
